import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Global analytics helper functions
window.trackEvent = window.trackEvent || function() {};
window.trackEcommerce = window.trackEcommerce || function() {};

export default function AnalyticsTracker() {
  const [config, setConfig] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const location = useLocation();

  // Fetch analytics config on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/analytics-config/active`);
        setConfig(response.data);
      } catch (error) {
        console.log('Analytics config not available');
      }
    };
    fetchConfig();
  }, []);

  // Initialize Google Analytics
  const initGA = useCallback((measurementId) => {
    if (window.gtag) return; // Already initialized
    
    // Load gtag script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', measurementId, {
      send_page_view: false // We'll send page views manually
    });

    console.log('Google Analytics initialized:', measurementId);
  }, []);

  // Initialize Meta Pixel
  const initMetaPixel = useCallback((pixelId) => {
    if (window.fbq) return; // Already initialized

    // Load Meta Pixel script
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');

    window.fbq('init', pixelId);

    console.log('Meta Pixel initialized:', pixelId);
  }, []);

  // Initialize analytics when config is available
  useEffect(() => {
    if (!config || initialized) return;

    if (config.google_analytics?.measurement_id) {
      initGA(config.google_analytics.measurement_id);
    }

    if (config.meta_pixel?.pixel_id) {
      initMetaPixel(config.meta_pixel.pixel_id);
    }

    // Set up global tracking functions
    window.trackEvent = (eventName, params = {}) => {
      // Google Analytics
      if (window.gtag && config.google_analytics) {
        window.gtag('event', eventName, params);
      }
      // Meta Pixel custom event
      if (window.fbq && config.meta_pixel) {
        window.fbq('trackCustom', eventName, params);
      }
    };

    window.trackEcommerce = (eventType, data) => {
      // E-commerce events for GA4
      if (window.gtag && config.google_analytics?.track_ecommerce) {
        switch (eventType) {
          case 'view_item':
            window.gtag('event', 'view_item', {
              currency: 'INR',
              value: data.price,
              items: [{
                item_id: data.id,
                item_name: data.name,
                price: data.price,
                quantity: 1
              }]
            });
            break;
          case 'add_to_cart':
            window.gtag('event', 'add_to_cart', {
              currency: 'INR',
              value: data.price * (data.quantity || 1),
              items: [{
                item_id: data.id,
                item_name: data.name,
                price: data.price,
                quantity: data.quantity || 1
              }]
            });
            break;
          case 'begin_checkout':
            window.gtag('event', 'begin_checkout', {
              currency: 'INR',
              value: data.total,
              items: data.items?.map(item => ({
                item_id: item.id,
                item_name: item.name,
                price: item.price,
                quantity: item.quantity
              }))
            });
            break;
          case 'purchase':
            window.gtag('event', 'purchase', {
              transaction_id: data.order_id,
              currency: 'INR',
              value: data.total,
              shipping: data.shipping || 0,
              items: data.items?.map(item => ({
                item_id: item.id,
                item_name: item.name,
                price: item.price,
                quantity: item.quantity
              }))
            });
            break;
        }
      }

      // Meta Pixel e-commerce events
      if (window.fbq && config.meta_pixel) {
        switch (eventType) {
          case 'view_item':
            if (config.meta_pixel.track_view_content) {
              window.fbq('track', 'ViewContent', {
                content_ids: [data.id],
                content_name: data.name,
                content_type: 'product',
                value: data.price,
                currency: 'INR'
              });
            }
            break;
          case 'add_to_cart':
            if (config.meta_pixel.track_add_to_cart) {
              window.fbq('track', 'AddToCart', {
                content_ids: [data.id],
                content_name: data.name,
                content_type: 'product',
                value: data.price * (data.quantity || 1),
                currency: 'INR'
              });
            }
            break;
          case 'begin_checkout':
            window.fbq('track', 'InitiateCheckout', {
              content_ids: data.items?.map(i => i.id),
              num_items: data.items?.length || 0,
              value: data.total,
              currency: 'INR'
            });
            break;
          case 'purchase':
            if (config.meta_pixel.track_purchases) {
              window.fbq('track', 'Purchase', {
                content_ids: data.items?.map(i => i.id),
                content_type: 'product',
                value: data.total,
                currency: 'INR',
                num_items: data.items?.length || 0
              });
            }
            break;
        }
      }
    };

    setInitialized(true);
  }, [config, initialized, initGA, initMetaPixel]);

  // Track page views on route change
  useEffect(() => {
    const pagePath = location.pathname + location.search;

    // Native page analytics — always on (no config required)
    const sendNativePageView = async () => {
      try {
        let sid = localStorage.getItem('nh_session_id');
        if (!sid) {
          sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
          localStorage.setItem('nh_session_id', sid);
        }
        const visitorId = localStorage.getItem('nh_visitor_id') || (() => {
          const v = Math.random().toString(36).slice(2) + Date.now().toString(36);
          localStorage.setItem('nh_visitor_id', v);
          return v;
        })();
        // Skip admin/pos pages — they're internal, not customer traffic
        if (
          pagePath.startsWith('/admin') ||
          pagePath.startsWith('/pos') ||
          pagePath.startsWith('/vendor')
        ) return;

        // Don't await — fire-and-forget so it never blocks navigation
        axios.post(`${API_URL}/api/track/pageview`, {
          path: pagePath,
          title: document.title,
          referrer: document.referrer || null,
          session_id: sid,
          visitor_id: visitorId,
          screen_width: window.innerWidth,
        }).catch(() => {});
      } catch (_e) { /* never break the app */ }
    };
    sendNativePageView();

    if (!config || !initialized) return;

    // Google Analytics page view
    if (window.gtag && config.google_analytics?.track_pageviews) {
      window.gtag('event', 'page_view', {
        page_path: pagePath,
        page_title: document.title
      });
    }

    // Meta Pixel page view
    if (window.fbq && config.meta_pixel?.track_pageviews) {
      window.fbq('track', 'PageView');
    }
  }, [location, config, initialized]);

  return null; // This component doesn't render anything
}

// Export helper hook for components to use
export function useAnalytics() {
  const trackEvent = useCallback((eventName, params = {}) => {
    if (window.trackEvent) {
      window.trackEvent(eventName, params);
    }
  }, []);

  const trackEcommerce = useCallback((eventType, data) => {
    if (window.trackEcommerce) {
      window.trackEcommerce(eventType, data);
    }
  }, []);

  return { trackEvent, trackEcommerce };
}
